import React from 'react';
import { CardHeader, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import HistorySearchIcon from '@mui/icons-material/YoutubeSearchedFor';

import { DataGrid, PageTitleWithToolTip } from '../Common';
import { StyledCellLink, Percentage, Weight } from '../Common/DataGrid/CellComponents';
import { text_commonCellProps } from '../Common/DataGrid/utils';
import { getRangeFilterOperator } from '../Common/DataGrid/FilterComponents';

import { Grey2 } from '../../color_utils';
import { useNavigate } from '../../hooks';
import Routes from '../../routes';
import LeakRateIcon from '../Common/DataGrid/CellComponents/LeakRateIcon';

const rangeFilterOperator = getRangeFilterOperator({});

const columns = [
    {
        field: 'name',
        headerName: 'Asset name',
        ...text_commonCellProps,
        renderCell: ({ row: { name, id } }) => (
            <StyledCellLink
                path={`/Inventory/${id}`}
                text={name}
                from={window.location.pathname}
            />
        )
    },
    {
        field: 'locationOnSite',
        headerName: 'Location on site',
        ...text_commonCellProps
    },
    {
        field: 'roomArea',
        headerName: 'Room/Area',
        ...text_commonCellProps
    },
    {
        field: 'status',
        headerName: 'Status',
        ...text_commonCellProps
    },
    {
        field: 'systemStatus',
        headerName: 'Operational Status',
        ...text_commonCellProps
    },
    {
        field: 'type',
        headerName: 'Type',
        ...text_commonCellProps
    },
    {
        field: 'temperatureClass',
        headerName: 'Temperature Class',
        ...text_commonCellProps
    },
    {
        field: 'materialType',
        headerName: 'Material Type',
        ...text_commonCellProps
    },
    {
        field: 'materialClass',
        headerName: 'Material Class',
        ...text_commonCellProps
    },
    {
        field: 'currentGasWeight',
        headerName: 'Current gas weight',
        filterOperators: rangeFilterOperator,
        renderCell: ({ row: { currentGasWeight } }) => (
            <Weight value={currentGasWeight} />
        )
    },
    {
        field: 'currentChargeCapacity',
        headerName: 'Gas capacity charge',
        filterOperators: rangeFilterOperator,
        renderCell: ({ row: { currentChargeCapacity } }) => (
            <Weight value={currentChargeCapacity} />
        )
    },
    {
        field: 'manufacturer',
        headerName: 'Manufacturer',
        ...text_commonCellProps
    },
    {
        field: 'modelNumber',
        headerName: 'Model number',
        ...text_commonCellProps
    },
    {
        field: 'serialNumber',
        headerName: 'Serial number',
        ...text_commonCellProps
    },
    {
        field: 'tagNumber',
        headerName: 'Tag number',
        ...text_commonCellProps
    },
    {
        field: 'leakRate',
        headerName: 'EPA Leak rate %',
        filterOperators: rangeFilterOperator,
        renderCell: ({ row: { leakRate } }) => <Percentage value={leakRate} />
    },
    {
        field: 'epaAnnualizedLeakRate',
        headerName: 'Indicators',
        ...text_commonCellProps,
        renderCell: ({ row: { epaAnnualizedLeakRate, assetTypeId } }) => <LeakRateIcon {...{ epaAnnualizedLeakRate, assetTypeId }} />
    },
]

const InventoryMechanical = ({
    id
}) => {

    const navigate = useNavigate({
        pageName: "inventoryMechanical"
    });

    const actionRow = {
        type: 'actions',
        field: 'Quick Actions',
        width: 100,
        getActions: ({ row }) => {

            const { id } = row;
            const quickActions = [
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="View Asset"
                        >
                            <SearchIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => navigate.push(`/Inventory/${id}`)}
                />,
                <GridActionsCellItem
                    icon={
                        <Tooltip
                            title="View History"
                        >
                            <HistorySearchIcon sx={{ color: Grey2 }} />
                        </Tooltip>
                    }
                    onClick={() => navigate.push(`/Inventory/${id}#History`)}
                />
            ];

            return quickActions;
        }
    }

    return (
        <>
            <DataGrid
                url={Routes.Location.InventoryTable(id, false, true, false, false)}
                tableHeader="Mechanical Equipment"
                noResultText="Oops...It is empty here..."
                noResultText2="Create a new Mechanical Equipment or change your search criteria."
                columns={[...columns, actionRow]}
                headerContent={
                    <CardHeader
                        title={
                            <PageTitleWithToolTip
                                pageTitle={"Mechanical Equipment"}
                            />
                        }
                        sx={{
                            "& .MuiCardHeader-action": { display: "flex" },
                            "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                        }}
                    />
                }
                hideExportButton={true}
            />
        </>
    )
}

export default InventoryMechanical;
